import { ArrowRight, Check, CircleAlert, CircleCheck, RotateCcw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { ApiError, confirmSheets } from "@/api/client";
import { useCan } from "@/auth/AuthProvider";
import { StepHeader } from "@/flow/StepHeader";
import {
  elapsed,
  finishedPath,
  hasStopped,
  isFinished,
  pagesSelected,
  stepsFor,
} from "@/flow/progress";
import type { ProgressStep } from "@/flow/progress";
import { useDocument } from "@/hooks/useDocument";
import { useRun } from "@/hooks/useRun";
import { Button } from "@/ui/Button";
import { cn } from "@/ui/cn";
import { Notice } from "@/ui/Notice";
import { Card, Skeleton, Spinner } from "@/ui/primitives";

const STATE_WORD: Record<ProgressStep["state"], string> = {
  waiting: "Waiting",
  active: "In progress",
  done: "Done",
  failed: "Stopped here",
};

/** How long the finished checklist stays on screen before the take-off opens. */
const HANDOVER_MS = 900;

/**
 * Step three: the run, as three things an estimator recognises. Opens the take-off by
 * itself when the run finishes; says what went wrong, and offers another go, when it
 * does not.
 */
export function ProcessingScreen() {
  const { id = "", runId = "" } = useParams();
  const navigate = useNavigate();
  const { doc } = useDocument(id);
  const { run, error } = useRun(runId);
  const canRun = useCan("documents:upload");
  const [now, setNow] = useState(() => Date.now());
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);

  const busy = run ? run.status === "queued" || run.status === "running" : true;
  useEffect(() => {
    if (!busy) {
      return;
    }
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [busy]);

  const finished = run ? isFinished(run) : false;
  const handTo = run ? finishedPath(id, run) : `/documents/${id}/runs/${runId}/takeoff`;
  useEffect(() => {
    if (!finished) {
      return;
    }
    const timer = setTimeout(() => void navigate(handTo, { replace: true }), HANDOVER_MS);
    return () => clearTimeout(timer);
  }, [finished, navigate, handTo]);

  const retry = useCallback(async () => {
    setRetrying(true);
    setRetryError(null);
    try {
      const next = await confirmSheets(id);
      await navigate(`/documents/${id}/runs/${next.id}/progress`, { replace: true });
    } catch (err) {
      setRetryError(err instanceof ApiError ? err.detail : "could not start another run");
    } finally {
      setRetrying(false);
    }
  }, [id, navigate]);

  const steps = run ? stepsFor(run) : [];
  const stopped = run ? hasStopped(run) : false;

  return (
    <main className="min-h-full bg-bg">
      <StepHeader
        step="processing"
        documentId={id}
        filename={doc?.filename}
        runId={runId}
        runFinished={finished}
      />
      <div className="mx-auto max-w-xl px-4 py-12">
        <Card className="overflow-hidden" aria-live="polite">
          <div className="flex items-center gap-3 border-b border-line px-6 py-5">
            <span
              aria-hidden="true"
              className={cn(
                "grid size-9 shrink-0 place-items-center rounded-full",
                finished
                  ? "bg-ok-soft text-ok"
                  : stopped
                    ? "bg-danger-soft text-danger"
                    : "bg-accent-soft text-accent",
              )}
            >
              {finished ? (
                <CircleCheck className="size-5" />
              ) : stopped ? (
                <CircleAlert className="size-5" />
              ) : (
                <Spinner />
              )}
            </span>
            <h1 className="text-lg font-semibold text-ink">
              {finished
                ? "Take-off ready"
                : stopped
                  ? run?.status === "capped"
                    ? "Stopped at the cost cap"
                    : "The run stopped"
                  : "Working through the sheets"}
            </h1>
          </div>

          <div className="px-6 py-5">
            {!run && !error ? (
              <div className="space-y-3" role="status" aria-label="Loading">
                <Skeleton className="h-10" />
                <Skeleton className="h-10" />
                <Skeleton className="h-10" />
              </div>
            ) : null}
            {error ? <Notice tone="danger">{error}</Notice> : null}

            {run ? (
              <>
                <ol className="relative flex flex-col gap-5">
                  {steps.map((step, i) => (
                    <li key={step.key} className="flex gap-3" data-state={step.state}>
                      <span
                        aria-hidden="true"
                        className={cn(
                          "grid size-7 shrink-0 place-items-center rounded-full text-xs font-semibold",
                          step.state === "done" && "bg-ok text-white",
                          step.state === "active" && "bg-accent text-accent-ink",
                          step.state === "failed" && "bg-danger text-white",
                          step.state === "waiting" && "border border-line-strong text-muted",
                        )}
                      >
                        {step.state === "done" ? (
                          <Check className="size-4" strokeWidth={3} />
                        ) : step.state === "failed" ? (
                          "!"
                        ) : step.state === "active" ? (
                          <Spinner className="size-3.5" />
                        ) : (
                          i + 1
                        )}
                      </span>
                      <div className="min-w-0 flex-1 pt-0.5">
                        <p
                          className={cn(
                            "text-sm font-medium",
                            step.state === "waiting" ? "text-muted" : "text-ink",
                          )}
                        >
                          {step.title}
                          <span className="sr-only">: {STATE_WORD[step.state]}</span>
                        </p>
                        <p className="text-[13px] text-muted">{step.detail}</p>
                        {step.key === "classify" && step.state === "active" ? (
                          <div
                            className="mt-2 h-1.5 overflow-hidden rounded-full bg-sunken"
                            role="progressbar"
                            aria-label="Sheets read"
                            aria-valuemin={0}
                            aria-valuemax={pagesSelected(run)}
                            aria-valuenow={Math.min(run.pages_classified, pagesSelected(run))}
                          >
                            <div
                              className="h-full rounded-full bg-accent transition-[width]"
                              style={{
                                width: `${Math.round(
                                  (Math.min(run.pages_classified, pagesSelected(run)) /
                                    Math.max(1, pagesSelected(run))) *
                                    100,
                                )}%`,
                              }}
                            />
                          </div>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ol>

                <dl className="mt-6 grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-lg bg-sunken px-3 py-2">
                    <dt className="text-[11px] font-medium tracking-wide text-muted uppercase">
                      Cost so far
                    </dt>
                    <dd className="tabular mt-0.5 font-medium text-ink">
                      ${run.cost_usd.toFixed(2)}{" "}
                      <span className="font-normal text-muted">
                        of ${run.cost_cap_usd.toFixed(2)} cap
                      </span>
                    </dd>
                  </div>
                  <div className="rounded-lg bg-sunken px-3 py-2">
                    <dt className="text-[11px] font-medium tracking-wide text-muted uppercase">
                      Time
                    </dt>
                    <dd className="tabular mt-0.5 font-medium text-ink">
                      {elapsed(
                        run.started_at ?? run.created_at,
                        run.finished_at ? Date.parse(run.finished_at) : now,
                      ) || "…"}
                    </dd>
                  </div>
                </dl>
              </>
            ) : null}

            {stopped && run ? (
              <div className="mt-6 flex flex-col gap-3" role="alert">
                <Notice tone="danger">
                  {run.error ??
                    (run.status === "capped"
                      ? "The run reached its cost cap before it finished."
                      : "The run stopped without saying why.")}
                </Notice>
                <div className="flex items-center gap-2">
                  {canRun ? (
                    <Button variant="primary" disabled={retrying} onClick={() => void retry()}>
                      {retrying ? <Spinner /> : <RotateCcw aria-hidden="true" />}
                      {retrying ? "Starting…" : "Try again"}
                    </Button>
                  ) : null}
                  <Button asChild variant="ghost">
                    <Link to={`/documents/${id}/sheets`}>Change the sheets</Link>
                  </Button>
                </div>
                {retryError ? <p className="text-[13px] text-danger">{retryError}</p> : null}
              </div>
            ) : null}

            {finished ? (
              <Button asChild variant="primary" className="mt-6 w-full">
                <Link to={`/documents/${id}/runs/${runId}/takeoff`}>
                  Open the take-off
                  <ArrowRight aria-hidden="true" />
                </Link>
              </Button>
            ) : null}
          </div>
        </Card>
      </div>
    </main>
  );
}
